import { createContext, useContext, type ReactNode } from "react";
import type { UseAdminAppReturn } from "../hook/useAdminApp";
import type { PSAuthConfig } from "@pineseed/workspace/handlers/auth-handler";
import type { AcpAvatarChatConfig } from "@pineseed/interface-gui/component/AcpAvatarChat";
import { useAccountAdmin, type AccountAdminHandler } from "../hook/useAccountAdmin";
import { useTaggerAdmin, type TaggerAdminHandler } from "../hook/useTaggerAdmin";
import { useParticipantAdmin, type ParticipantAdminHandler } from "../hook/useParticipantAdmin";
import { useGroupAdmin, type GroupAdminHandler } from "../hook/useGroupAdmin";
import { useThreadAdmin, type ThreadAdminHandler } from "../hook/useThreadAdmin";
import { useIntegratorAdmin, type IntegratorAdminHandler } from "../hook/useIntegratorAdmin";
import { useComposerAdmin, type ComposerAdminHandler } from "../hook/useComposerAdmin";
import { useReaderAdmin, type ReaderAdminHandler } from "../hook/useReaderAdmin";
import { useIdentifierAdmin, type IdentifierAdminHandler } from "../hook/useIdentifierAdmin";
import { useMessengerAdmin, type MessengerAdminHandler } from "../hook/useMessengerAdmin";
import { useWorkflowAdmin, type WorkflowAdminHandler } from "../hook/useWorkflowAdmin";
import { usePromptAdmin, type PromptAdminHandler } from "../hook/usePromptAdmin";
import { useConnectorAdmin, type ConnectorAdminHandler } from "../hook/useConnectorAdmin";
import { useCloudProviderAdmin, type CloudProviderAdminHandler } from "../hook/useCloudProviderAdmin";
import { useTraceAdmin, type TraceAdminHandler } from "../hook/useTraceAdmin";

export interface AdminContextValue {
  adminApp: UseAdminAppReturn;
  authConfig: PSAuthConfig;
  acpAvatarChatConfig: AcpAvatarChatConfig;
  isAuthenticated: boolean;
  account: AccountAdminHandler;
  tagger: TaggerAdminHandler;
  participant: ParticipantAdminHandler;
  group: GroupAdminHandler;
  thread: ThreadAdminHandler;
  integrator: IntegratorAdminHandler;
  composer: ComposerAdminHandler;
  reader: ReaderAdminHandler;
  identifier: IdentifierAdminHandler;
  messenger: MessengerAdminHandler;
  workflow: WorkflowAdminHandler;
  prompt: PromptAdminHandler;
  connector: ConnectorAdminHandler;
  cloudProvider: CloudProviderAdminHandler;
  trace: TraceAdminHandler;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export interface AdminProviderProps {
  children: ReactNode;
  adminApp: UseAdminAppReturn;
  authConfig: PSAuthConfig;
  acpAvatarChatConfig: AcpAvatarChatConfig;
  isAuthenticated: boolean;
}

export function AdminProvider({ children, adminApp, authConfig, acpAvatarChatConfig, isAuthenticated }: AdminProviderProps) {
  const account = useAccountAdmin(adminApp, authConfig);
  const tagger = useTaggerAdmin(adminApp);
  const participant = useParticipantAdmin(adminApp);
  const group = useGroupAdmin(adminApp);
  const thread = useThreadAdmin(adminApp);
  const integrator = useIntegratorAdmin(adminApp);
  const composer = useComposerAdmin(adminApp);
  const reader = useReaderAdmin(adminApp);
  const identifier = useIdentifierAdmin(adminApp);
  const messenger = useMessengerAdmin(adminApp);
  const workflow = useWorkflowAdmin(adminApp);
  const prompt = usePromptAdmin(adminApp);
  const connector = useConnectorAdmin(adminApp, account.accounts, account.organizations);
  const cloudProvider = useCloudProviderAdmin(adminApp, account.accounts);
  const trace = useTraceAdmin(adminApp);

  const value: AdminContextValue = {
    adminApp,
    authConfig,
    acpAvatarChatConfig,
    isAuthenticated,
    account,
    tagger,
    participant,
    group,
    thread,
    integrator,
    composer,
    reader,
    identifier,
    messenger,
    workflow,
    prompt,
    connector,
    cloudProvider,
    trace,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextValue {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
}

